import { CargoController } from "@/app/ui/controllers/CargoController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { AddBagOrStt } from "@/data/payload/api/CargoApiRequest";
import {
  CargoDetailStt,
  CheckEligibleReadyToCargo
} from "@/domain/entities/Cargo";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { RequestCheckEligibleReadyToCargo } from "@/domain/entities/ReadyToCargo";
import { nextTick } from "vue";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { AccountController } from '@/app/ui/controllers/AccountController';
import { createI18n } from 'vue-i18n';
import en from "@/i18n/locales/en";
import id from "@/i18n/locales/id";
import my from "@/i18n/locales/my";

class CargoComposable {
  get bagResult(): {
    baggingNumber: string;
    bagGrossWeight: number;
    stt: CargoDetailStt[];
  }[] {
    return CargoController.bagResult;
  }

  get sttResult(): CargoDetailStt[] {
    return CargoController.sttResultFinal;
  }

  async checkEligible(
    callback: (res: CheckEligibleReadyToCargo) => void = () => undefined,
    close?: () => void | undefined,
    ineligible?: () => void | undefined
  ) {
    const i18nInstance = createI18n({
      legacy: false,
      locale: AccountController.accountData.account_type_detail.countryCode.toLowerCase(),
      fallbackLocale: "id",
      globalInjection: false,
      messages: {
        en,
        id,
        my
      }
    });
    const translate = i18nInstance.global.t;
    const tempSttResult = [...this.sttResult];
    const tempBagResult = [...this.bagResult];

    const stts = tempSttResult
      .filter((stt) => !stt.baggingNumber)
      .map((stt) => {
        return new AddBagOrStt(undefined, stt.sttNumber);
      });
    const bags = tempBagResult.map((bag) => {
      return new AddBagOrStt(bag.baggingNumber);
    });

    const payload = new RequestCheckEligibleReadyToCargo({
      bagOrStt: [...stts, ...bags]
    });

    try {
      MainAppController.closeErrorMessage();
      MainAppController.closeMessageModal();
      MainAppController.showLoading();
      const res = await CargoController.checkEligible(payload);
      if (res.isEligible) {
        callback(res);
      } else {
        const stts = res.bagOrStt.map((item) => item.sttNo);
        const bags = res.bagOrStt.map((item) => item.bagNo);

        const sttResult = tempSttResult.filter((stt) => {
          if (!stts.length) return [];
          return stts.includes(stt.sttNumber);
        });

        const bagResult = tempSttResult
          .filter((bag) => !!bag.baggingNumber)
          .filter((bag) => {
            if (!bags.length) return [];
            return bags.includes(bag.baggingNumber);
          });

        for (const stt of sttResult) {
          CargoController.setItemToDelete(stt);
          await nextTick();
          window.dispatchEvent(new Event("delete-stt"));
          await nextTick();
          await new Promise((resolve) => {
            setTimeout(() => {
              resolve(true);
            }, 200);
          });
        }

        for (const bag of bagResult) {
          CargoController.setItemToDelete(bag);
          await nextTick();
          window.dispatchEvent(new Event("delete-stt"));
          await nextTick();
          await new Promise((resolve) => {
            setTimeout(() => {
              resolve(true);
            }, 200);
          });
        }
        ineligible?.();

        MainAppController.showMessageModal(
          new ModalMessageEntities({
            image: "badge-warning-lg",
            title: translate(
              "cargoV2.components.common.sttBagCannotUpdateTitle"
            ),
            message: translate(
              "cargoV2.components.common.sttBagCannotUpdateMessage"
            ),
            textSuccess: "Cek Data",
            onSubmit: async () => {
              MainAppController.closeMessageModal();
              close?.();
            }
          })
        );
      }
    } catch (err) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(err, translate("cargoV2.components.common.errorCheckEligible"), () =>
          this.checkEligible()
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }
}

export function useCargo() {
  const composable = new CargoComposable();

  return {
    checkEligible: composable.checkEligible.bind(composable)
  };
}
